import React, { useRef, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiEdit3, FiUpload, FiX } from "react-icons/fi";
import { apiV1 } from "api-v2";

type CoverPageMode = "global" | "default" | "none" | "upload";

interface CoverPagePanelProps {
	mode: CoverPageMode;
	globalMode: string | undefined;
	onModeChange: (mode: CoverPageMode) => void;
	coverPageFile: File | null;
	coverPagePreviewUrl: string | null;
	onCoverPageChange: (file: File | null, previewUrl: string | null) => void;
	coverPageFileId: number | null;
	onCoverPageFileIdChange: (fileId: number | null) => void;
	onEditCoverPage?: () => void;
}

const MODE_LABELS: Record<string, string> = {
	none: "No cover page",
	default: "Default branded",
	upload: "Uploaded document",
};

export const CoverPagePanel: React.FC<CoverPagePanelProps> = ({
	mode,
	globalMode,
	onModeChange,
	coverPageFile,
	coverPagePreviewUrl,
	onCoverPageChange,
	coverPageFileId,
	onCoverPageFileIdChange,
	onEditCoverPage,
}) => {
	const globalModeLabel = MODE_LABELS[globalMode ?? "none"] ?? "No cover page";
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);

	const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			return;
		}

		// Validate file type
		if (file.type !== "application/pdf") {
			setUploadError("Only PDF files are accepted.");
			return;
		}

		setIsUploading(true);
		setUploadError(null);

		try {
			const formData = new FormData();
			formData.append("cover_page", file);
			const response = await apiV1.post("reports/shortlist/cover-page", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const data = response.data?.data ?? response.data;
			onCoverPageChange(file, URL.createObjectURL(file));
			onCoverPageFileIdChange(data.file_id);
			onModeChange("upload");
		} catch {
			setUploadError("Failed to upload cover page. Please try again.");
		} finally {
			setIsUploading(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleRemoveFile = () => {
		onCoverPageChange(null, null);
		onCoverPageFileIdChange(null);
		onModeChange("global");
	};

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
				Cover Page
			</h3>
			<div className="flex flex-col gap-2">
				<label className="flex items-center gap-2 text-sm cursor-pointer">
					<input
						type="radio"
						name="cover_page_mode"
						value="global"
						checked={mode === "global"}
						onChange={() => onModeChange("global")}
						className="h-4 w-4 focus:ring-1 focus:ring-blue-500"
					/>
					<span>
						Use global setting
						<span className="ml-1 text-xs text-gray-400">
							({globalModeLabel})
						</span>
					</span>
				</label>
				<label className="flex items-center gap-2 text-sm cursor-pointer">
					<input
						type="radio"
						name="cover_page_mode"
						value="upload"
						checked={mode === "upload"}
						onChange={() => {
							if (coverPageFileId) {
								onModeChange("upload");
							} else {
								fileInputRef.current?.click();
							}
						}}
						className="h-4 w-4 focus:ring-1 focus:ring-blue-500"
					/>
					Upload document
				</label>
				<label className="flex items-center gap-2 text-sm cursor-pointer">
					<input
						type="radio"
						name="cover_page_mode"
						value="default"
						checked={mode === "default"}
						onChange={() => onModeChange("default")}
						className="h-4 w-4 focus:ring-1 focus:ring-blue-500"
					/>
					Default branded cover
				</label>
				<label className="flex items-center gap-2 text-sm cursor-pointer">
					<input
						type="radio"
						name="cover_page_mode"
						value="none"
						checked={mode === "none"}
						onChange={() => onModeChange("none")}
						className="h-4 w-4 focus:ring-1 focus:ring-blue-500"
					/>
					No cover page
				</label>
			</div>

			{/* Edit Cover Page button — visible when cover mode resolves to "default" */}
			{(mode === "default" || (mode === "global" && globalMode === "default")) && onEditCoverPage && (
				<button
					type="button"
					onClick={onEditCoverPage}
					className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
				>
					<FiEdit3 className="h-4 w-4" />
					Edit Cover Page
				</button>
			)}

			{/* Upload area */}
			{mode === "upload" && (
				<div className="mt-3">
					{coverPageFile ? (
						<div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
							<span className="truncate text-sm text-gray-700">{coverPageFile.name}</span>
							<button
								type="button"
								onClick={handleRemoveFile}
								className="ml-2 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
								aria-label="Remove cover page"
							>
								<FiX className="h-4 w-4" />
							</button>
						</div>
					) : (
						<button
							type="button"
							onClick={() => fileInputRef.current?.click()}
							disabled={isUploading}
							className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 transition hover:border-blue-400 hover:text-blue-600"
						>
							{isUploading ? (
								<>
									<Spinner size="xs" />
									Uploading...
								</>
							) : (
								<>
									<FiUpload className="h-4 w-4" />
									Choose PDF file
								</>
							)}
						</button>
					)}
					{uploadError && (
						<p className="mt-1 text-xs text-red-500">{uploadError}</p>
					)}
				</div>
			)}

			<input
				ref={fileInputRef}
				type="file"
				accept="application/pdf"
				onChange={handleFileSelect}
				className="hidden"
			/>
		</div>
	);
};

